// src/features/automacaodeemails/api.js
export const API_BASE = String(
  process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || "/api"
).replace(/\/+$/, "");
const BASE = "/automacao-emails";

function joinUrl(base, path) {
  const p = String(path || "");
  if (!p) return base;
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  const a = base.endsWith("/") ? base.slice(0, -1) : base;
  const b = p.startsWith("/") ? p : `/${p}`;
  return `${a}${b}`;
}

function buildUrl(path) {
  return joinUrl(API_BASE, path);
}

async function readBody(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function buildError(res, data) {
  const msg =
    (data && typeof data === "object" && (data.error || data.message)) ||
    (typeof data === "string" && data) ||
    `HTTP ${res.status}`;
  const err = new Error(msg);
  err.status = res.status;
  err.body = data;
  return err;
}

async function request(method, path, body, opts = {}) {
  const url = joinUrl(API_BASE, path);

  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    signal: opts.signal,
  });

  const data = await readBody(res);

  if (!res.ok) throw buildError(res, data);
  return data;
}

// Helpers basicos
const get = (path, opts) => request("GET", path, null, opts);
const post = (path, body, opts) => request("POST", path, body, opts);
const put = (path, body, opts) => request("PUT", path, body, opts);
const patch = (path, body, opts) => request("PATCH", path, body, opts);
const del = (path, opts) => request("DELETE", path, null, opts);

const safeGet = async (path, fallback = []) => {
  try {
    const r = await get(path);
    return r?.data ?? r ?? fallback;
  } catch (e) {
    if (e?.status === 404) return fallback;
    throw e;
  }
};

/* ============================================================
   API ORGANIZADA POR DOMINIO
   ============================================================ */
export const api = {
  // Helpers basicos
  get,
  post,
  put,
  patch,
  del,
  safeGet,
  buildUrl,

  /* ========== TELAS DO SISTEMA ========== */
  screens: {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return get(`${BASE}/screens${qs ? `?${qs}` : ""}`);
    },
    catalog: () => get(`${BASE}/screens/catalog`),
    sync: (opts = {}) => {
      const qs = new URLSearchParams(opts.params || {}).toString();
      const headers = opts.baseUrl ? { "x-base-url": opts.baseUrl } : undefined;
      return post(`${BASE}/screens/sync${qs ? `?${qs}` : ""}`, {}, { headers });
    },
    get: (id) => get(`${BASE}/screens/${id}`),
    create: (data) => post(`${BASE}/screens`, data),
    update: (id, data) => put(`${BASE}/screens/${id}`, data),
    delete: (id) => del(`${BASE}/screens/${id}`),
    scan: (id) => post(`${BASE}/screens/${id}/scan`),
    elements: (id) => get(`${BASE}/screens/${id}/elements`),
    previewUrl: (id, url, exclude) => {
      const params = new URLSearchParams({ mode: "fullpage" });
      if (url) params.set("url", url);
      if (exclude && exclude.length) {
        params.set("exclude", JSON.stringify(exclude));
      }
      return buildUrl(`${BASE}/screens/${id}/preview?${params.toString()}`);
    },
  },

  /* ========== ELEMENTOS DETECTADOS ========== */
  elements: {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return get(`${BASE}/elements${qs ? `?${qs}` : ""}`);
    },
    get: (id) => get(`${BASE}/elements/${id}`),
    previewUrl: (id, mode = "image") =>
      buildUrl(`${BASE}/elements/${id}/preview?mode=${encodeURIComponent(mode)}`),
    delete: (id) => del(`${BASE}/elements/${id}`),
    validate: (id, is_valid) => patch(`${BASE}/elements/${id}/validate`, { is_valid }),
    stats: () => get(`${BASE}/elements/stats`),
  },

  /* ========== AUTOMACOES ========== */
  automations: {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return get(`${BASE}/automations${qs ? `?${qs}` : ""}`);
    },
    get: (id) => get(`${BASE}/automations/${id}`),
    create: (data) => post(`${BASE}/automations`, data),
    update: (id, data) => put(`${BASE}/automations/${id}`, data),
    delete: (id) => del(`${BASE}/automations/${id}`),
    toggle: (id) => patch(`${BASE}/automations/${id}/toggle`),
    test: (id, testEmail) => post(`${BASE}/automations/${id}/test`, { test_email: testEmail }),
    execute: (id) => post(`${BASE}/automations/${id}/execute`),
    logs: (id, params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return get(`${BASE}/automations/${id}/logs${qs ? `?${qs}` : ""}`);
    },
  },

  /* ========== LOGS ========== */
  logs: {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return get(`${BASE}/logs${qs ? `?${qs}` : ""}`);
    },
    get: (id) => get(`${BASE}/logs/${id}`),
    stats: (days = 7) => get(`${BASE}/logs/stats?days=${days}`),
    cleanup: (daysToKeep = 90) => post(`${BASE}/logs/cleanup`, { days_to_keep: daysToKeep }),
  },

  /* ========== CONFIGURACOES ========== */
  settings: {
    list: () => get(`${BASE}/settings`),
    get: (key) => get(`${BASE}/settings/${key}`),
    update: (key, value) => put(`${BASE}/settings/${key}`, { value }),
    bulk: (settings) => post(`${BASE}/settings/bulk`, { settings }),
    testSmtp: (testEmail) => post(`${BASE}/settings/test-smtp`, { test_email: testEmail }),
  },

  /* ========== GATILHOS (CATALOGO) ========== */
  triggers: {
    catalog: () => get(`${BASE}/triggers/catalog`),
  },

  /* ========== ASSINATURAS / IMAGENS ========== */
  assets: {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return safeGet(`${BASE}/assets${qs ? `?${qs}` : ""}`, []);
    },
    get: (id) => get(`${BASE}/assets/${id}`),
    create: (data) => post(`${BASE}/assets`, data),
    update: (id, data) => put(`${BASE}/assets/${id}`, data),
    delete: (id) => del(`${BASE}/assets/${id}`),
  },

  /* ========== EMAILS BASE (DESTINATARIOS) ========== */
  emailsBase: {
    list: () => safeGet(`${BASE}/emails-base`, []),
  },

  /* ========== TURMAS (GRUPOS) ========== */
  turmas: {
    list: () => safeGet(`${BASE}/turmas`, []),
  },
};
